import { useCallback, useEffect, useState } from "react";

import {
  supabaseAuthenticatedApi,
  workspaceAgentUrl,
} from "../components/SupabaseClient";

export function encodeModelRepoPath(repoId) {
  return String(repoId || "")
    .split("/")
    .map((part) => encodeURIComponent(part))
    .join("/");
}

export async function listModelCaches({ timeout = 15000 } = {}) {
  const response = await supabaseAuthenticatedApi.get(
    "/model-cache",
    workspaceAgentUrl,
    timeout,
  );
  return response.json();
}

export async function getModelCacheDetail(repoId, { timeout = 15000 } = {}) {
  const response = await supabaseAuthenticatedApi.get(
    `/model-cache/${encodeModelRepoPath(repoId)}`,
    workspaceAgentUrl,
    timeout,
  );
  return response.json();
}

export async function getModelCacheStatus({ timeout = 15000 } = {}) {
  const response = await supabaseAuthenticatedApi.get(
    "/model-cache/status",
    workspaceAgentUrl,
    timeout,
  );
  return response.json();
}

export async function importModelCache(
  { repoId, revision = "main", forceDownload = false },
  { timeout = 5000 } = {},
) {
  const response = await supabaseAuthenticatedApi.post(
    "/model-cache/imports",
    {
      repo_id: repoId,
      revision,
      force_download: forceDownload,
    },
    workspaceAgentUrl,
    timeout,
  );
  return response.json();
}

export async function getModelImportStatus(importId, { timeout = 5000 } = {}) {
  const response = await supabaseAuthenticatedApi.get(
    `/model-cache/imports/${encodeURIComponent(importId)}/status`,
    workspaceAgentUrl,
    timeout,
  );
  return response.json();
}

export async function deleteModelCache(
  repoId,
  { revision = null, timeout = 30000 } = {},
) {
  const query = revision ? `?revision=${encodeURIComponent(revision)}` : "";
  const response = await supabaseAuthenticatedApi.delete(
    `/model-cache/${encodeModelRepoPath(repoId)}${query}`,
    workspaceAgentUrl,
    timeout,
  );
  return response.json();
}

export async function scanModelCache({ timeout = 300000 } = {}) {
  const response = await supabaseAuthenticatedApi.post(
    "/model-cache/scan",
    {},
    workspaceAgentUrl,
    timeout,
  );
  return response.json();
}

export async function evictModelCache(
  { dryRun = false } = {},
  { timeout = 300000 } = {},
) {
  const response = await supabaseAuthenticatedApi.post(
    `/model-cache/evict?dry_run=${dryRun ? "true" : "false"}`,
    {},
    workspaceAgentUrl,
    timeout,
  );
  return response.json();
}

export function useModelCaches() {
  const [models, setModels] = useState([]);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refreshModels = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await listModelCaches();
      setCacheInfo(payload);
      setModels(Array.isArray(payload?.models) ? payload.models : []);
    } catch (err) {
      console.error("Failed to load model cache:", err);
      setError(err.message || "Failed to load model cache.");
      setModels([]);
      setCacheInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshModels();
  }, [refreshModels]);

  return {
    cacheInfo,
    error,
    loading,
    models,
    refreshModels,
  };
}

export function useModelCacheDetails() {
  const [detailsByRepo, setDetailsByRepo] = useState({});
  const [loadingRepo, setLoadingRepo] = useState("");
  const [errorsByRepo, setErrorsByRepo] = useState({});

  const loadModelDetail = useCallback(
    async (repoId) => {
      if (!repoId || detailsByRepo[repoId] || loadingRepo === repoId) {
        return;
      }

      setLoadingRepo(repoId);
      setErrorsByRepo((prev) => ({ ...prev, [repoId]: "" }));
      try {
        const detail = await getModelCacheDetail(repoId);
        setDetailsByRepo((prev) => ({ ...prev, [repoId]: detail }));
      } catch (err) {
        console.error("Failed to load model detail:", err);
        setErrorsByRepo((prev) => ({
          ...prev,
          [repoId]: err.message || "Failed to load model details.",
        }));
      } finally {
        setLoadingRepo("");
      }
    },
    [detailsByRepo, loadingRepo],
  );

  return {
    detailsByRepo,
    errorsByRepo,
    loadModelDetail,
    loadingRepo,
  };
}
